import { StandaloneSearchBox } from '@react-google-maps/api';
import { useRef } from 'react';

import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';

interface CustomInputProperties {
  inputLabel?: string;
  placeholder: string;
  value: string | number | null;
  name: string;
  onChangeValue: (
    name: string,
    address: string | number,
    latitude: number | null,
    longitude: number | null,
  ) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  error?: boolean;
  errorMessage?: string;
}

/**
 * Custom address input box with google places api integrated
 */
function CustomAddressInput({
  inputLabel,
  name,
  placeholder,
  value,
  onChangeValue,
  valueRequired,
  readOnly,
  error,
  errorMessage,
}: CustomInputProperties) {
  const inputReference = useRef<google.maps.places.SearchBox | null>(null);

  const { isLoaded } = useGoogleMaps();

  /**
   * Handle input change
   */
  const handleValueChange = (changeValue: string | number) => {
    onChangeValue(name, changeValue, null, null);
  };

  /**
   * Handles reference load for the StandaloneSearchBox.
   */
  const handleReferenceLoad = (reference: google.maps.places.SearchBox | null) => {
    inputReference.current = reference;
  };

  /**
   * Just a place change
   */
  const handlePlaceChange = () => {
    if (inputReference.current) {
      const places = inputReference.current.getPlaces();

      if (places?.length) {
        const place = places[0];

        const formatted = place.formatted_address || place.name || '';

        let lat: number | null = null;
        let lng: number | null = null;

        if (place.geometry?.location) {
          lat = place.geometry.location.lat();
          lng = place.geometry.location.lng();
        }

        onChangeValue(name, formatted, lat, lng);
      }
    }
  };

  return (
    <div className="custom-address-input-wrap">
      {inputLabel && (
        <p className={`input-label ${error && 'error'}`}>
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className={`custom-input-container ${error && 'error'}`}>
        {isLoaded && (
          <StandaloneSearchBox
            onLoad={(searchBox) => handleReferenceLoad(searchBox as google.maps.places.SearchBox)}
            onPlacesChanged={handlePlaceChange}>
            <input
              type="text"
              placeholder={placeholder}
              value={value !== null ? value : undefined}
              onChange={(event) => handleValueChange(event.target.value)}
              required={valueRequired}
              readOnly={readOnly}
            />
          </StandaloneSearchBox>
        )}
      </div>
      {error && <p className="custom-input-error-message">{errorMessage}</p>}
    </div>
  );
}

CustomAddressInput.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  error: false,
  errorMessage: '',
};

export default CustomAddressInput;
